import { type ClassValue, clsx } from "clsx";

/**
 * Function to merge class names using clsx
 * @param inputs - The class names to merge
 * @returns The merged class names
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Sets and removes data from the browser local storage.
 */
export const storage = {
  /**
   * Accepts name, value and sets them in Local Storage
   * @param name - The key name for the storage item
   * @param value - The value to store (will be JSON stringified)
   * @returns The result of localStorage.setItem
   */
  setItem: (name: string, value: string | number | boolean | object | null) => {
    return localStorage.setItem(name, JSON.stringify(value));
  },
  /**
   * Accepts name and gets item from Local Storage
   * @param name - The key name for the storage item
   * @returns The parsed value from storage, or empty string if not found
   */
  getItem: (name: string) => {
    const item = localStorage.getItem(name);
    if (!item) return "";

    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  },
  /**
   * Accepts name and clears item from Local Storage
   * @param name - The key name for the storage item to remove
   */
  clearItem: (name: string) => {
    if (localStorage.getItem(name)) {
      localStorage.removeItem(name);
    }
  },
};

/**
 * Gets a CSS variable value from the root element
 */
export const getCSSVariable = (variableName: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
};
